/**
 * Custom hook for managing project files and folders with drag & drop support.
 *
 * Key features:
 * - Folder structure preservation on drag & drop
 * - Batch upload with completion tracking
 * - Directory navigation and file selection
 * - Search and filtering capabilities
 *
 * Technical notes:
 * - Uses `useFsAccessApi: false` for Playwright compatibility
 * - Implements custom `getFilesFromEvent` to intercept folder drops
 * - Uses batch callbacks to prevent premature UI refetches
 */

import { Accept } from "react-dropzone";

import { useProjectFilesData } from "./use-project-files-data";
import { useProjectFilesDropzone } from "./use-project-files-dropzone";

interface ProjectFilesProps {
  initialPaths?: string[];
  enableSettingParams?: boolean;
  accept?: Accept;
}

export function useProjectFiles({
  initialPaths = [],
  enableSettingParams,
  accept,
}: ProjectFilesProps) {
  const filesData = useProjectFilesData({ initialPaths, enableSettingParams });

  const dropzone = useProjectFilesDropzone({
    path: filesData.path,
    accept,
    fileDirectoryQuery: filesData.fileDirectoryQuery,
    fileDirectoryQueryDirectly: filesData.fileDirectoryQueryDirectly,
  });

  return {
    ...filesData,
    ...dropzone,
  };
}
